import { useMemo } from "react";
import * as LucideIcons from "lucide-react";
import useRouterData from "@/hooks/api/useRouterData";
import { useHrNav } from "@/features/hr/hooks/useHrNav";
import {
  applyBranchPipelineContext,
  useSidebarBranchContext,
} from "@/features/branchPipeline/sidebarPipelines";

/** The CRM's Settings tab, rendered pinned in `SidebarFooter` rather than inline. */
export const CRM_SETTINGS_PATH = "/settings";

export interface SidebarSection {
  label: string;
  /** Heading shown in place of `label` (a branch pipeline that took over STAGES). */
  title?: string;
  group: string;
  routes: any[];
}

function resolveHrIcon(name: string) {
  return (
    (LucideIcons as unknown as Record<string, LucideIcons.LucideIcon>)[name] ??
    LucideIcons.FileText
  );
}

/**
 * The sidebar's section list for whichever suite is active, plus that suite's
 * pinned footer entry.
 *
 * THE TWO SUITES SHARE NO ROUTES. Inside HR this returns HR's own manifest and
 * nothing else — not the CRM's STAGES, not its MODULE group, and not its
 * CONFIGURATIONS. HR has its own Automation, Reports and Settings screens under
 * `/hr/*` (see `features/hr/config/hr.nav.ts`); borrowing the CRM's would point
 * an HR admin at lead pipelines.
 *
 * What IS shared is the rendering: both suites feed the same
 * `SideBarAccordan` / `Collapsed` components, so HR inherits the CRM's
 * accordion, indentation, active pill and green edge bar and cannot drift from
 * them. Same chrome, separate contents.
 *
 * NO HR, NO CHANGE. The CRM's Settings is lifted into the pinned footer only
 * while the suite switcher is showing (the user can open HR), so both suites
 * end with Settings in the same place. Everyone else gets the CRM's groups
 * exactly as the server sent them — Settings inside its group, nothing
 * filtered — i.e. the sidebar as it was before HR existed.
 */
export function useSidebarSections(
  opts: {
    /**
     * Keep the CRM's Settings inside its group instead of lifting it into the
     * pinned footer slot — for surfaces that render sections WITHOUT the
     * sidebar footer (the leads full-screen navigator).
     */
    includeSettings?: boolean;
  } = {},
): {
  sections: SidebarSection[];
  settings: any | null;
} {
  const includeSettings = opts.includeSettings ?? false;
  const { tabs } = useRouterData();
  const {
    isHrActive,
    canAccessHr,
    sections: hrSections,
    pinnedItem,
  } = useHrNav();
  // Branch pipelines: which pipeline(s) STAGES shows follows the header branch
  // picker. No-op for a tenant without branch pipelines.
  const branchContext = useSidebarBranchContext();

  return useMemo(() => {
    if (isHrActive) {
      return {
        sections: hrSections.map((section) => ({
          label: section.label,
          group: `hr-${section.group}`,
          routes: section.items.map((item) => ({
            key: item.routeKey,
            path: item.path,
            label: item.label,
            icon: resolveHrIcon(item.icon),
          })),
        })),
        settings: pinnedItem
          ? {
              key: pinnedItem.routeKey,
              path: pinnedItem.path,
              label: pinnedItem.label,
              icon: resolveHrIcon(pinnedItem.icon),
            }
          : null,
      };
    }

    const groups: SidebarSection[] = applyBranchPipelineContext(
      (tabs?.groups ?? []) as SidebarSection[],
      branchContext,
    );

    // Byte-for-byte the pre-HR sidebar: no switcher, no lift.
    if (includeSettings || !canAccessHr) {
      return { sections: groups, settings: null };
    }

    // Switcher showing: lift the CRM's Settings into the footer. Only a group
    // the lift emptied is dropped — a group that arrived empty stays as sent.
    let settings: any | null = null;
    const emptiedByLift = new Set<SidebarSection>();
    const sections = groups
      .map((g) => {
        const routes = g.routes ?? [];
        if (!routes.some((r) => r?.path === CRM_SETTINGS_PATH)) return g;
        const kept = routes.filter((r) => {
          if (r?.path !== CRM_SETTINGS_PATH) return true;
          settings ??= r;
          return false;
        });
        const next = { ...g, routes: kept };
        if (kept.length === 0) emptiedByLift.add(next);
        return next;
      })
      .filter((g) => !emptiedByLift.has(g));

    return { sections, settings };
  }, [tabs, isHrActive, canAccessHr, hrSections, pinnedItem, includeSettings, branchContext]);
}
